import { useState, useEffect } from 'react';
import SignageDesign from '../../../models/SignageDesign';
import { transformSignageDesignComponents } from '../utils/componentMapper';

/**
 * Custom hook for fetching and managing signage data
 * @param {Object} client - FreespaceClient instance
 * @param {string} screenId - Screen ID
 * @param {Object} signageState - Signage state
 * @returns {Object} Signage data and loading states
 */
export const useSignageData = (client, screenId, signageState) => {
  const [signageDesign, setSignageDesign] = useState(null);
  const [transformedComponents, setTransformedComponents] = useState({ 
    mainComponents: [], 
    sideComponents: [],
    allComponents: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!client || !screenId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch design data using FreespaceClient
        const designData = await client.fetchSignageDesignVersion(screenId);
        
        // Create SignageDesign model from JSON response
        const design = SignageDesign.fromJSON(signageState, designData);
        setSignageDesign(design);
        
        // Transform components using model
        const transformed = transformSignageDesignComponents(design);
        setTransformedComponents(transformed);
        
      } catch (error) {
        console.error('Error fetching signage design:', error);
        setError(error.message || 'Failed to fetch signage data');
        
        // Set empty state on error
        setTransformedComponents({ 
          mainComponents: [], 
          sideComponents: [],
          allComponents: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [client, screenId, signageState]);

  return {
    signageDesign,
    transformedComponents,
    loading,
    error
  };
};
